import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, LogOut } from 'lucide-react';
import Button from './Button';

function ConfirmationModal({
  isOpen,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
  isLoading = false,
}) {
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;

    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const isDanger = confirmVariant === 'danger' || /logout/i.test(confirmLabel) || /logout/i.test(title);
  const AccentIcon = isDanger ? LogOut : AlertTriangle;
  const accentIconClasses = isDanger
    ? 'bg-[#fff3ef] text-[#9a3b32] border-[#f1d4cb]'
    : 'bg-[#f7f4ef] text-[#6f5644] border-[#e7ddd3]';
  const confirmButtonClasses = isDanger
    ? 'border-[#9a3b32] bg-[#9a3b32] text-white shadow-[0_14px_28px_rgba(154,59,50,0.18)] hover:bg-[#862f27] hover:translate-y-[-1px]'
    : 'border-[#6f5644] bg-[#6f5644] text-white shadow-[0_14px_28px_rgba(111,86,68,0.16)] hover:bg-[#5f4a3b] hover:translate-y-[-1px]';
  const cancelButtonClasses =
    'border-[#ddd3c9] bg-white text-[#3f342d] shadow-[0_10px_24px_rgba(17,17,17,0.06)] hover:bg-[#faf7f3] hover:border-[#cfc2b6]';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[rgba(17,17,17,0.26)] px-4 py-6 backdrop-blur-[4px]">
      <div className="w-full max-w-[430px] cursor-default select-none overflow-hidden rounded-[30px] border border-[#e9e0d8] bg-[linear-gradient(180deg,#fdfcfb_0%,#faf6f1_100%)] shadow-[0_24px_60px_rgba(17,17,17,0.14)]">
        <div className="border-b border-[#ebe3db] px-5 py-5 sm:px-6">
          <div className="flex items-start gap-4">
            <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-[20px] border ${accentIconClasses}`}>
              <AccentIcon className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="m-0 text-[1.18rem] font-semibold tracking-[-0.02em] text-[#111111] sm:text-[1.28rem]">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-[#7f736b]">{description}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2.5 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
          <Button
            className={`w-full justify-center rounded-[16px] px-4.5 py-2.5 text-[14px] font-semibold sm:w-auto ${cancelButtonClasses}`.trim()}
            variant="secondary"
            type="button"
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
          <Button
            className={`w-full justify-center rounded-[16px] px-4.5 py-2.5 text-[14px] font-semibold sm:w-auto ${confirmButtonClasses}`.trim()}
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ConfirmationModal;
